import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Play } from "lucide-react";
import { PageHeader } from "@/components/shared/PageHeader";
import { EmptyState } from "@/components/shared/States";
import { useSeo } from "@/hooks/useSeo";
import { fetchContent, GALLERY_CATEGORIES } from "@/lib/contentApi";

export default function Gallery() {
  useSeo("Gallery", "Photos and videos from ASAI expert sessions, workshops, internships and events.");
  const [cat, setCat] = useState("All");
  const [items, setItems] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => { fetchContent("gallery", cat).then(setItems).catch(() => setItems([])); }, [cat]);
  const cats = ["All", ...GALLERY_CATEGORIES];

  return (
    <div data-testid="page-gallery">
      <PageHeader overline="Gallery" titleLines={["Moments from", "the ASAI journey"]} description="A look at our sessions, workshops, internships and events." />
      <section className="bg-white py-16 md:py-24">
        <div className="container-asai">
          <div className="mb-8 flex flex-wrap gap-2" data-testid="gallery-filters">
            {cats.map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`gallery-cat-${c.replace(/\s+/g, "-").toLowerCase()}`}
                className={`rounded-full px-4 py-2 text-xs font-medium transition-colors ${cat === c ? "bg-orange text-white" : "border border-navy/15 text-navy/70 hover:border-orange"}`}>{c}</button>
            ))}
          </div>
          {items === null ? null : items.length === 0 ? (
            <EmptyState title="No media yet" description="Photos and videos will appear here as our programs and events grow." />
          ) : (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4" data-testid="gallery-grid">
              {items.map((it) => (
                <button key={it.id} onClick={() => setActive(it)} className="group relative aspect-square overflow-hidden rounded-xl bg-lightgrey" data-testid="gallery-item">
                  <img src={it.thumbnail || it.url} alt={it.alt || it.title || "ASAI gallery"} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/60 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
                  {it.type === "video" && <span className="absolute inset-0 flex items-center justify-center"><Play className="h-10 w-10 text-white drop-shadow" /></span>}
                  {it.title && <span className="absolute inset-x-2 bottom-2 truncate text-left text-xs font-medium text-white opacity-0 transition-opacity group-hover:opacity-100">{it.title}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setActive(null)}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 p-4" data-testid="gallery-lightbox">
            <button className="absolute right-5 top-5 text-white/80 hover:text-white" onClick={() => setActive(null)}><X className="h-7 w-7" /></button>
            <motion.div initial={{ scale: 0.94 }} animate={{ scale: 1 }} className="max-h-[85vh] w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
              {active.type === "video" ? (
                <video src={active.url} controls autoPlay className="max-h-[85vh] w-full rounded-xl" />
              ) : (
                <img src={active.url} alt={active.alt || active.title || "ASAI"} className="max-h-[85vh] w-full rounded-xl object-contain" />
              )}
              {active.title && <p className="mt-3 text-center text-sm text-white/80">{active.title}</p>}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
